import { createClient } from '@supabase/supabase-js';

/**
 * Portal Supabase client. Targets the CLOUD (production) platform project by
 * default — this is the platform-admin console for the live platform, so a
 * platform_admins row in cloud must resolve against cloud auth, not local
 * Docker. Override for local work by exporting ADMIN_SUPABASE_URL /
 * ADMIN_SUPABASE_ANON_KEY before `node admin-portal/serve.mjs` (serve.mjs
 * injects them at build time), or by setting a `window.__ENV__` block in
 * index.html. The anon key is a public client key (safe to ship); every
 * privileged read/write goes through the platform-admin RPCs, which enforce
 * `is_platform_admin()` server-side.
 */
const winEnv =
  (window as unknown as { __ENV__?: { SUPABASE_URL?: string; SUPABASE_ANON_KEY?: string } }).__ENV__ ?? {};

// Injected at build time by serve.mjs; empty unless ADMIN_SUPABASE_* is exported.
const buildUrl = process.env.ADMIN_SUPABASE_URL;
const buildAnonKey = process.env.ADMIN_SUPABASE_ANON_KEY;

// Default target: the cloud (production) project.
const CLOUD_SUPABASE_URL = 'https://sfnkybapahglghrehejy.supabase.co';
const CLOUD_SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNmbmt5YmFwYWhnbGdocmVoZWp5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI2MjE2MDIsImV4cCI6MjA4ODE5NzYwMn0.rQ2ylAXbVblDBOCEXCtFmFLK1fWTosPzMgamF0nS9m4';

const SUPABASE_URL = winEnv.SUPABASE_URL || buildUrl || CLOUD_SUPABASE_URL;
const SUPABASE_ANON_KEY = winEnv.SUPABASE_ANON_KEY || buildAnonKey || CLOUD_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/**
 * Where the portal's own edge functions live. Normally `<project>/functions/v1`;
 * ADMIN_FUNCTIONS_URL (or window.__ENV__.FUNCTIONS_URL) points it at a function
 * run standalone with Deno, because the local Docker edge runtime serves one
 * checkout only (the one `supabase start` ran from).
 */
const winFunctionsUrl = (winEnv as { FUNCTIONS_URL?: string }).FUNCTIONS_URL;
export const FUNCTIONS_URL = (winFunctionsUrl || process.env.ADMIN_FUNCTIONS_URL || `${SUPABASE_URL}/functions/v1`).replace(/\/$/, '');
export const SUPABASE_ANON = SUPABASE_ANON_KEY;

/** Coarse target for the UI: 'local' Docker vs the 'cloud' (production) project. */
const isLocalTarget = /127\.0\.0\.1|localhost|\[::1\]/.test(SUPABASE_URL);
export const SUPABASE_ENV = {
  url: SUPABASE_URL,
  target: isLocalTarget ? ('local' as const) : ('cloud' as const),
};
